import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, ReactNode } from 'react';

import { GroupsPlaceholder } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { customScrollStyles, InfoBlock, SearchInput } from '@gem/ui-kit';

import { ChipsWithAvatar } from 'APP/components/ChipsWithAvatar/ChipsWithAvatar';
import { List } from 'APP/components/List/List';
import { GroupType } from 'APP/model/group/groupModel.types';
import { User } from 'STORE/Users/User/User';

import { useSelectWorkspaceMembersPresenter } from './SelectWorkspaceMembers.presenter';
import styles from './SelectWorkspaceMembers.styles.m.css';
import { WorkspaceMemberWithCheckbox } from './WorkspaceMemberWithCheckbox/WorkspaceMemberWithCheckbox';

interface ISelectWorkspaceMembersProps {
  children?: ReactNode;
  selectedMembersIds: string[];
  targetGroupType: GroupType;
  setSelectedIds(ids: string[]): void;
  groupId: string;
}

export const SelectWorkspaceMembers: FC<ISelectWorkspaceMembersProps> = observer(
  ({ children, ...props }) => {
    const presenter = useSelectWorkspaceMembersPresenter(props);
    const { t } = useTranslation();

    const renderItem = useCallback((user: User) => {
      return (
        <WorkspaceMemberWithCheckbox
          member={user}
          toggleMember={presenter.toggleContact}
          getIsSelected={presenter.getIsSelected}
        />
      );
    }, []);

    return (
      <>
        <SearchInput
          className={styles.search}
          placeholder={t('search_hint')}
          value={presenter.searchText}
          onChange={presenter.onSearch}
        />
        <ChipsWithAvatar
          chips={presenter.chips}
          onRemove={presenter.removeChips}
        />
        {children}
        <div className={classNames(styles.container, customScrollStyles.container)}>
          <List
            isLoading={presenter.isLoading}
            hasMore={presenter.hasMore}
            data={presenter.members}
            onEndReached={presenter.loadMore}
            keyExtractor={(member: User): string => member.id}
            renderItem={renderItem}
            ListEmptyComponent={<InfoBlock title={t('no_results_found')} />}
            SpinnerComponent={
              <GroupsPlaceholder
                className={styles.placeholders}
                count={presenter.members.length > 0 ? 2 : 10}
              />
            }
          />
        </div>
      </>
    );
  }
);
